import { BrowserRouter, Link, Navigate, Outlet, Route, Routes, useLocation } from "react-router-dom";
import { Toaster } from "sonner";
import { Logo } from "@/components/brand/Logo";
import { NetworkGuards, RouteErrorBoundary } from "@/components/layout/Resilience";
import "@/App.css";
import { AuthProvider, useAuth } from "@/context/AuthContext";
import { ProfileProvider, useProfiles } from "@/context/ProfileContext";
import { GuestProvider, useGuest } from "@/context/GuestContext";
import { GuestGate } from "@/components/auth/GuestGate";
import AppLayout from "@/components/layout/AppLayout";
import { Spinner } from "@/components/catalog/TitleGrid";
import AuthCallback from "@/pages/AuthCallback";
import Login from "@/pages/Login";
import Profiles from "@/pages/Profiles";
import Home from "@/pages/Home";
import Browse from "@/pages/Browse";
import Detail from "@/pages/Detail";
import Watch from "@/pages/Watch";
import Library from "@/pages/Library";
import Search from "@/pages/Search";
import Account from "@/pages/Account";
import { lazy, Suspense, useEffect, useState } from "react";
import { PlatformBranding, useCreatorOn, useMaintenance } from "@/lib/platform";
import { UploadProvider } from "@/features/uploads/UploadContext";
import Pricing from "@/pages/Pricing";
import { BillingCancel, BillingSuccess } from "@/pages/BillingResult";
import { BillingProvider } from "@/context/BillingContext";
import LiveEvents from "@/pages/LiveEvents";
import { LiveEventDetail, LiveWatch } from "@/pages/LiveEventDetail";
import { ForgotPassword, InfoPage, NotificationsPage, ResetPassword } from "@/pages/MiscPages";

const named = (loader, name) => lazy(() => loader().then((m) => ({ default: m[name] })));
const CreateHome = lazy(() => import("@/features/create/CreateHome"));
const ProjectWorkspace = lazy(() => import("@/features/create/ProjectWorkspace"));
const ConsoleLayout = named(() => import("@/features/admin/ConsoleLayout"), "ConsoleLayout");
const AdminDashboard = lazy(() => import("@/features/admin/AdminDashboard"));
const AdminBilling = lazy(() => import("@/features/admin/AdminBilling"));
const AdminTitles = lazy(() => import("@/features/admin/AdminTitles"));
const TitleEditor = lazy(() => import("@/features/admin/TitleEditor"));
const AdminHomepage = lazy(() => import("@/features/admin/AdminHomepage"));
const AdminLive = lazy(() => import("@/features/admin/AdminLive"));
const AdminPages = () => import("@/features/admin/AdminPages");
const AdminMedia = named(AdminPages, "AdminMedia");
const AdminModeration = named(AdminPages, "AdminModeration");
const AdminUsers = named(AdminPages, "AdminUsers");
const StudioUploads = named(AdminPages, "StudioUploads");
const SharedBible = lazy(() => import("@/pages/SharedBible"));
const OwnerApp = lazy(() => import("@/features/owner/OwnerApp"));
const AdminSupport = lazy(() => import("@/features/admin/AdminSupport"));
const NotFound = lazy(() => import("@/pages/NotFound"));
const MfaGate = named(() => import("@/features/owner/SecurityScreens"), "MfaGate");
const MaintenancePage = named(() => import("@/features/owner/SecurityScreens"), "MaintenancePage");
const InviteAccept = named(() => import("@/features/owner/SecurityScreens"), "InviteAccept");
const Loading = <div className="min-h-screen bg-[#050505]"><Spinner /></div>;

const RequireAuth = ({ children }) => {
  const { user } = useAuth();
  const { guestOn } = useGuest();
  const location = useLocation();
  if (user === null || (user === false && guestOn === undefined)) return <div className="min-h-screen bg-[#050505]"><Spinner /></div>;
  if (user === false) return guestOn ? <GuestGate /> : <Navigate to="/login" replace state={{ from: location.pathname }} />;
  return children;
};

const RequireProfile = ({ children }) => {
  const { profiles, activeProfile } = useProfiles();
  if (profiles === null) return <div className="min-h-screen bg-[#050505]"><Spinner /></div>;
  if (!activeProfile) return <Navigate to="/profiles" replace />;
  return children;
};

const PublicOnly = ({ children }) => {
  const { user } = useAuth();
  if (user) return <Navigate to="/profiles" replace />;
  return children;
};

const Protected = ({ children }) => <RequireAuth><RequireProfile>{children}</RequireProfile></RequireAuth>;

// Creator side retired by the Owner: its routes show the branded 404 while the master flag is off.
const CreatorOnly = ({ children }) => (useCreatorOn() ? children : <NotFound />);

// Browsable without an account when the Owner allows guest browsing; signed-in users keep the profile gate.
const Viewer = ({ children }) => {
  const { isGuest } = useGuest();
  return isGuest ? children : <Protected>{children}</Protected>;
};

const SupportShell = () => {
  const { user } = useAuth();
  if (user) return <Protected><AppLayout /></Protected>;
  return (
    <div data-testid="public-support-shell" className="min-h-screen bg-[#050505] text-white">
      <header className="flex items-center justify-between px-4 py-5 sm:px-8"><Logo /><Link to="/login" data-testid="public-support-login" className="btn-ghost-gold px-5 py-2 text-sm">Sign in</Link></header>
      <Outlet />
    </div>
  );
};

function AppRouter() {
  const location = useLocation();
  const { user, checkAuth } = useAuth();
  const maint = useMaintenance(location.pathname);
  useEffect(() => {
    const mfa = () => checkAuth();
    window.addEventListener("aetryx:mfa", mfa);
    return () => window.removeEventListener("aetryx:mfa", mfa);
  }, [checkAuth]);
  if (location.hash?.includes("session_id=")) return <AuthCallback />;
  if (user?.mfa_pending) return <MfaGate />;
  if (maint && user !== null && !["admin", "owner"].includes(user?.role) && !location.pathname.startsWith("/login")) return <MaintenancePage />;
  return (
    <RouteErrorBoundary resetKey={location.pathname}>
    <Suspense fallback={Loading}>
    <Routes>
      <Route path="/invite/:token" element={<InviteAccept />} />
      <Route path="/share/bible/:token" element={<CreatorOnly><SharedBible /></CreatorOnly>} />
      <Route path="/support" element={<SupportShell />}><Route index element={<InfoPage page="support" />} /></Route>
      <Route path="/login" element={<PublicOnly><Login mode="login" /></PublicOnly>} />
      <Route path="/register" element={<PublicOnly><Login mode="register" /></PublicOnly>} />
      <Route path="/forgot-password" element={<PublicOnly><ForgotPassword /></PublicOnly>} />
      <Route path="/reset-password" element={<ResetPassword />} />
      <Route path="/profiles" element={<RequireAuth><Profiles /></RequireAuth>} />
      <Route path="/watch/:slug/:episodeId?" element={<Viewer><Watch /></Viewer>} />
      <Route path="/live-events/:slug/watch" element={<Viewer><LiveWatch /></Viewer>} />
      <Route path="/admin" element={<RequireAuth><ConsoleLayout mode="admin" /></RequireAuth>}>
        <Route index element={<AdminDashboard />} />
        <Route path="titles" element={<AdminTitles />} />
        <Route path="titles/new" element={<TitleEditor mode="admin" key="admin-new" />} />
        <Route path="titles/:slug" element={<TitleEditor mode="admin" />} />
        <Route path="originals" element={<AdminTitles originals />} />
        <Route path="homepage" element={<AdminHomepage />} />
        <Route path="moderation" element={<AdminModeration />} />
        <Route path="media" element={<AdminMedia />} />
        <Route path="users" element={<AdminUsers />} />
        <Route path="subscriptions" element={<AdminBilling />} />
        <Route path="live" element={<AdminLive />} />
        <Route path="support" element={<AdminSupport />} />
      </Route>
      <Route path="/studio" element={<CreatorOnly><RequireAuth><ConsoleLayout mode="studio" /></RequireAuth></CreatorOnly>}>
        <Route index element={<Navigate to="/studio/uploads" replace />} />
        <Route path="uploads" element={<StudioUploads />} />
        <Route path="titles/new" element={<TitleEditor mode="studio" key="studio-new" />} />
        <Route path="titles/:slug" element={<TitleEditor mode="studio" />} />
      </Route>
      <Route element={<Viewer><AppLayout /></Viewer>}>
        <Route path="/" element={<Home />} />
        <Route path="/originals" element={<Browse kind="originals" />} />
        <Route path="/movies" element={<Browse kind="movies" />} />
        <Route path="/series" element={<Browse kind="series" />} />
        <Route path="/shorts" element={<Browse kind="shorts" />} />
        <Route path="/title/:slug" element={<Detail />} />
        <Route path="/search" element={<Search />} />
        <Route path="/live-events" element={<LiveEvents />} />
        <Route path="/live-events/:slug" element={<LiveEventDetail />} />
        <Route path="/about" element={<InfoPage page="about" />} />
        <Route path="/pricing" element={<Pricing />} />
      </Route>
      <Route element={<Protected><AppLayout /></Protected>}>
        <Route path="/library" element={<Library />} />
        <Route path="/account" element={<Account />} />
        <Route path="/notifications" element={<NotificationsPage />} />
        <Route path="/billing/success" element={<BillingSuccess />} />
        <Route path="/billing/cancel" element={<BillingCancel />} />
        <Route path="/owner/*" element={<OwnerApp />} />
      </Route>
      <Route path="/create" element={<CreatorOnly><Protected><AppLayout><CreateHome /></AppLayout></Protected></CreatorOnly>} />
      <Route path="/create/p/:id" element={<CreatorOnly><Protected><AppLayout><ProjectWorkspace /></AppLayout></Protected></CreatorOnly>} />
      <Route path="*" element={<NotFound />} />
    </Routes>
    </Suspense>
    </RouteErrorBoundary>
  );
}

export default function App() {
  return (
    <div className="App">
      <BrowserRouter>
        <AuthProvider>
          <ProfileProvider>
            <GuestProvider>
            <BillingProvider>
              <UploadProvider>
                <PlatformBranding />
                <NetworkGuards />
                <AppRouter />
              </UploadProvider>
            </BillingProvider>
            </GuestProvider>
            <Toaster
              theme="dark"
              position="top-center"
              toastOptions={{ style: { background: "rgba(11,11,13,0.95)", border: "1px solid rgba(212,175,55,0.35)", color: "#fff", fontFamily: "Manrope, sans-serif" } }}
            />
          </ProfileProvider>
        </AuthProvider>
      </BrowserRouter>
    </div>
  );
}
